import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppStore } from "../../../../providers/AppStore";
import {
  generateLogicServices,
  getHistoryPrescription,
  getHistoryPrescriptions,
} from "../../../../common/network/Api";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import ActionButton from "../../../../common/ui/ActionButton";
import InfoIcon from "../../svg/InfoIcon";
import CustomTooltip from "../common/CustomTooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ClientSidePanelToggle from "../common/ClientSidePanelToggle";
import PrescriptionDetailsView, {
  PrescriptionActionScope,
} from "./PrescriptionDetailsView";
import { useClientSidePanel } from "../../../../providers/ClientSidePanelProvider";
import PrescriptionDetailsViewSkeleton from "./PrescriptionDetailsViewSkeleton";
import PrescriptionTable from "./PrescriptionTable";
import { createPortal } from "react-dom";
import { useHistoryToolbarSlots } from "../history/HistoryToolbar";
import HistoryBanner from "../history/HistoryBanner";
import useHistoryScope from "../history/useHistoryScope";
import useHistoryList, {
  historyLoadingRestText,
  invalidateHistoryList,
} from "../history/useHistoryList";
import HistoryNotReadyState from "../history/HistoryNotReadyState";
import useHistoryClientName from "../history/useHistoryClientName";
import {
  HistoryPrescriptionDetail,
  HistoryPrescriptionListItem,
} from "../../entities/history";

/**
 * History → Prescription: the listing and the detail page, in all three
 * scopes.
 *
 * Data comes from the `/history/prescription` endpoint, every page of it
 * (`useHistoryList`), scoped by `userid` to the workspace subject (client or
 * the customer themself) and unscoped in the cross-client view. The detail is
 * its own request (`/history/prescription/{id}`), so a pasted URL works cold.
 *
 * What the viewer can do depends on the scope. The client workspace has
 * everything; the cross-client page keeps the actions that need only the
 * record (delete, status, edit/delete an item — keyed by the record's own
 * `customerID`) but not Regenerate or "Add new service/panel", which need the
 * ClientSidePanel and a selected client; a customer reads their own history.
 */
interface PrescriptionTabProps {
  /**
   * Remembers the listing's column filters across navigation. Passed down
   * rather than derived here: the key includes the History scope, which this
   * component does not read.
   */
  filterPersistKey?: string;
}

const PrescriptionTab = ({ filterPersistKey }: PrescriptionTabProps) => {
  const theme: Theme = useTheme();
  const { prescriptionId } = useParams<{ prescriptionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
    ASSetSnackBarActions,
    ASGetDataFromPlatform,
  } = useAppStore();
  const { setOnPrescriptionSaved } = useClientSidePanel();
  const toolbarSlots = useHistoryToolbarSlots();
  const scope = useHistoryScope();
  const canAct = scope.kind === "client";
  const detailActions: PrescriptionActionScope =
    scope.kind === "client"
      ? "workspace"
      : scope.kind === "company"
      ? "record"
      : "none";

  const {
    rows,
    loading,
    loadingRest,
    totalCount,
    truncated,
    error: listError,
    reload,
  } = useHistoryList({
    fetchPage: getHistoryPrescriptions,
    cacheKey: "prescription",
    userid: scope.kind === "company" ? undefined : scope.subjectId,
    enabled: scope.kind === "company" || Boolean(scope.subjectId),
  });

  // The open record. Fetched by id, independent of the list.
  const [detail, setDetail] = useState<HistoryPrescriptionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailNotFound, setDetailNotFound] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Set by the detail view around its own mutations (status toggle, delete).
  const [mutating, setMutating] = useState(false);
  // Sequences detail requests so a slow reply for a record the user has
  // already left can't land on the one now open.
  const detailRequestRef = useRef(0);
  // The cross-client page names the client on the record; the workspace
  // shells already do so in their top bar.
  const { name: clientName, resolving: clientNameResolving } =
    useHistoryClientName(
    detail?.customerID,
    scope.kind === "company"
  );

  const loadDetail = useCallback(
    async (id: string, silent = false) => {
      const requestId = ++detailRequestRef.current;
      const isCurrent = () => requestId === detailRequestRef.current;
      try {
        if (!silent) setDetailLoading(true);
        setDetailError(null);
        setDetailNotFound(false);
        const response = await getHistoryPrescription(id);
        if (!isCurrent()) return;
        if (!response || !response.id) {
          setDetail(null);
          setDetailNotFound(true);
        } else {
          setDetail(response);
        }
      } catch (err: any) {
        if (!isCurrent()) return;
        console.error("Error loading prescription:", err);
        setDetail(null);
        if (err?.response?.status === 404) {
          setDetailNotFound(true);
        } else {
          setDetailError(
            getApiErrorMessage(err, "Couldn't load this prescription.")
          );
        }
      } finally {
        if (isCurrent() && !silent) setDetailLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (prescriptionId) {
      loadDetail(prescriptionId);
    } else {
      setDetail(null);
      setDetailNotFound(false);
      setDetailError(null);
    }
  }, [prescriptionId, loadDetail]);

  /**
   * After a mutation: fresh detail if one is open, fresh report data, and the
   * list refetched in the background. The list is not awaited — on the
   * cross-client page that is every page of the company's prescriptions, and
   * the open record must not sit behind a skeleton for that long.
   */
  const refetchAll = useCallback(async () => {
    // Saving, answering or deleting re-creates or removes the prescription's
    // lab panels, and the banner here moves their status: the Assessments
    // list must not come back with its cached rows.
    invalidateHistoryList("labpanels");
    void reload().catch(() => {
      // The hook reports list errors through its own `error`.
    });
    if (prescriptionId) await loadDetail(prescriptionId, true);
    if (ASSelectedClient?.id && ASSelectedClient?.role !== undefined) {
      ASGetDataFromPlatform(ASSelectedClient.id, ASSelectedClient.role, true);
    }
  }, [reload, prescriptionId, loadDetail, ASSelectedClient, ASGetDataFromPlatform]);

  // Set callback for when prescription is saved from NewPrescriptionWidget
  useEffect(() => {
    if (!canAct) return;
    setOnPrescriptionSaved(() => async () => {
      await refetchAll();
    });
    return () => {
      setOnPrescriptionSaved(undefined);
    };
  }, [setOnPrescriptionSaved, refetchAll, canAct]);

  const goBackToList = () => {
    const basePath = location.pathname.replace(`/${prescriptionId}`, "");
    navigate(basePath + location.search);
  };

  const handleRegenerate = (onSuccess?: () => void) => {
    // Show confirmation before regenerating
    ASSetSnackBarMessage({
      title: "Regenerate Prescription",
      message:
        "Are you sure you want to regenerate the prescription? This will update it to reflect recent changes made to its services.",
    });
    ASSetTypeSnackBar("warning");
    ASSetSnackBarActions({
      onConfirm: async () => {
        if (!ASSelectedClient?.id) {
          return;
        }

        try {
          setMutating(true);
          await generateLogicServices(ASSelectedClient.id);

          // On success, refetch prescription data
          await refetchAll();

          // Call success callback if provided (e.g., to navigate back)
          if (onSuccess) {
            onSuccess();
          }

          // Show success message
          ASSetSnackBarMessage({
            title: "Success",
            message: "Prescription regenerated successfully!",
          });
          ASSetTypeSnackBar("success");
          ASSetOpenSnackBar(true);
        } catch (err) {
          console.error("Error regenerating prescription:", err);

          // Show error message
          ASSetSnackBarMessage({
            title: "Error",
            message:
              err instanceof Error
                ? err.message
                : "Failed to regenerate prescription. Please try again.",
          });
          ASSetTypeSnackBar("alert");
          ASSetOpenSnackBar(true);
        } finally {
          setMutating(false);
        }
      },
    });
    ASSetOpenSnackBar(true);
  };

  const banners = (
    <>
      {listError && (
        <HistoryBanner tone="warning" text={listError} />
      )}
      {loadingRest && (
        <HistoryBanner tone="info" text={historyLoadingRestText(rows.length, totalCount)} />
      )}
      {truncated && (
        <HistoryBanner tone="info" text="Only the most recent prescriptions are shown." />
      )}
    </>
  );

  // ---- Detail page ----
  if (prescriptionId) {
    // Only the detail needs a full-page skeleton. The listing keeps its
    // header and filters on screen and skeletons just the rows.
    // Skeleton from the very first render (the fetch starts in an effect, so
    // `detailLoading` alone would flash "not found" for a frame) until the
    // record and the badge's name are both in, so the header renders in one go.
    const detailPending = !detail && !detailNotFound && !detailError;
    if (detailPending || detailLoading || mutating || clientNameResolving) {
      return <PrescriptionDetailsViewSkeleton />;
    }

    if (detailError) {
      return (
        <HistoryNotReadyState
          title="Couldn't load this prescription"
          text={detailError}
        />
      );
    }

    if (detailNotFound || !detail) {
      return (
        <HistoryNotReadyState
          title="Prescription not found"
          text="This prescription is no longer available. Go back to Prescription to see the current list."
        />
      );
    }

    // The most recent auto-generated prescription is the one Regenerate
    // replaces. Picked by date rather than position, so it doesn't depend on
    // the order the pages arrived in.
    const latestAutoGenerated = rows.reduce<
      HistoryPrescriptionListItem | undefined
    >(
      (latest, row) =>
        row.sourceType === 1 &&
        (!latest ||
          new Date(row.dateCreated).getTime() >
            new Date(latest.dateCreated).getTime())
          ? row
          : latest,
      undefined
    );
    const isLatestAutoGenerated =
      detail.sourceType === 1 && latestAutoGenerated?.id === detail.id;

    return (
      <PrescriptionDetailsView
        prescription={detail}
        onBack={goBackToList}
        onRegenerate={() => handleRegenerate(goBackToList)}
        onDelete={refetchAll}
        isLatestAutoGenerated={isLatestAutoGenerated}
        setLoading={setMutating}
        actions={detailActions}
        clientName={clientName}
      />
    );
  }

  // ---- Listing ----
  return (
    <div style={styles.container}>
      {/* Regenerate / New Prescription live on History's tab line, not in a row
          of their own above the table — a header row here pushed every record
          down. Both act on the one client this listing is scoped to, so they
          exist only in the client workspace. */}
      {canAct &&
        toolbarSlots.actions &&
        createPortal(
          <>
            <ActionButton
              label={"Regenerate"}
              icon={
                <RefreshIcon
                  sx={{ color: theme.colors_ds.PRIMARY_ICON_MAIN }}
                />
              }
              iconRight={
                <CustomTooltip
                  message={
                    "Update the prescription to reflect recent changes made to its services."
                  }
                >
                  <InfoIcon />
                </CustomTooltip>
              }
              onClick={() => handleRegenerate()}
            />
            <ClientSidePanelToggle />
          </>,
          toolbarSlots.actions
        )}

      <PrescriptionTable
        rows={rows}
        loading={loading || mutating}
        showClient={!scope.isSingleSubject}
        banners={banners}
        clearFiltersSlot={toolbarSlots.filters}
        filterPersistKey={filterPersistKey}
      />
    </div>
  );
};

export default PrescriptionTab;

const styles = {
  // Fills the bounded History screen; it used to size itself to the viewport,
  // which overshot by the tab strip's height and made the page scroll.
  container: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
